import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Ban, ArrowLeft } from 'lucide-react';
import { getFeatureFlagForPath } from '@/client/config/featureFlags';
import ComingSoon from './ComingSoon';

/**
 * Gates admin page content based on the feature flag config. Placed once inside
 * AdminLayout, so it governs every admin route by looking at the current path:
 *
 *   - 'enabled'     → renders the page as-is.
 *   - 'coming-soon' → renders the designed ComingSoon page.
 *   - 'disabled'    → renders an "unavailable" notice (route is also hidden from
 *                     the sidebar, but this covers direct visits/bookmarks).
 */
const FeatureDisabled: React.FC<{ label?: string }> = ({ label }) => {
  const navigate = useNavigate();
  return (
    <div className="flex min-h-[70vh] w-full items-center justify-center px-6 py-16">
      <div className="w-full max-w-md text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100">
          <Ban className="h-8 w-8 text-gray-400" />
        </div>
        <h1 className="mb-2 text-2xl font-bold text-gray-900">
          {label ? `${label} is unavailable` : 'This page is unavailable'}
        </h1>
        <p className="mb-8 text-sm text-gray-600">
          This feature has been turned off. If you think this is a mistake, contact
          an administrator.
        </p>
        <button
          onClick={() => navigate('/admin')}
          className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-gray-800"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to dashboard
        </button>
      </div>
    </div>
  );
};

const FeatureGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const flag = getFeatureFlagForPath(pathname);

  if (!flag || flag.status === 'enabled') {
    return <>{children}</>;
  }

  if (flag.status === 'coming-soon') {
    return (
      <ComingSoon
        feature={flag.label}
        title={flag.comingSoon?.title}
        description={flag.comingSoon?.description}
        eta={flag.comingSoon?.eta}
      />
    );
  }

  // disabled
  return <FeatureDisabled label={flag.label} />;
};

export default FeatureGate;
